import { io, Socket } from 'socket.io-client'
import { WebSocketMessage } from '@/types/conversation'

const WS_URL = import.meta.env.VITE_WS_URL || window.location.origin

class SocketService {
  private socket: Socket | null = null
  private listeners: Map<string, Set<(data: any) => void>> = new Map()

  connect(url: string = WS_URL) {
    if (this.socket?.connected) return

    this.socket = io(url, {
      path: '/ws',
      transports: ['polling', 'websocket'], // Try polling first, then upgrade to websocket
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    })

    this.setupEventHandlers()
  }

  private setupEventHandlers() {
    if (!this.socket) return

    this.socket.on('connect', () => {
      console.log('WebSocket connected:', this.socket?.id)
      this.emit('connected', { id: this.socket?.id })
    })

    this.socket.on('disconnect', () => {
      console.log('WebSocket disconnected')
      this.emit('disconnected', {})
    })

    this.socket.on('error', (error) => {
      console.error('WebSocket error:', error)
      this.emit('error', error)
    })

    // Handle conversation updates
    this.socket.on('conversation:update', (data: WebSocketMessage) => {
      this.emit('conversation:update', data)
    })

    // Handle specific event types
    this.socket.on('bpmn:update', (data) => {
      this.emit('bpmn:update', data)
    })

    this.socket.on('audit:entry', (data) => {
      this.emit('audit:entry', data)
    })

    this.socket.on('stage:transition', (data) => {
      this.emit('stage:transition', data)
    })

    this.socket.on('subscribed', (data) => {
      this.emit('subscribed', data)
    })

    this.socket.on('admin:sessions', (data) => {
      this.emit('admin:sessions', data)
    })

    this.socket.on('session:update', (data) => {
      this.emit('session:update', data)
    })

    this.socket.on('system:message', (data) => {
      this.emit('system:message', data)
    })
  }

  subscribeToConversation(conversationId: string) {
    if (!this.socket) {
      console.error('Socket not connected')
      return
    }
    this.socket.emit('subscribe:conversation', conversationId)
  }

  unsubscribeFromConversation(conversationId: string) {
    if (!this.socket) return
    this.socket.emit('unsubscribe:conversation', conversationId)
  }

  subscribeToAdmin(adminKey?: string) {
    if (!this.socket) {
      console.error('Socket not connected')
      return
    }
    this.socket.emit('subscribe:admin', adminKey)
  }

  on(event: string, callback: (data: any) => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)?.add(callback)
  }

  off(event: string, callback: (data: any) => void) {
    this.listeners.get(event)?.delete(callback)
  }

  private emit(event: string, data: any) {
    this.listeners.get(event)?.forEach((callback) => {
      try {
        callback(data)
      } catch (error) {
        console.error(`Error in listener for ${event}:`, error)
      }
    })
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
    this.listeners.clear()
  }

  isConnected(): boolean {
    return this.socket?.connected || false
  }

  getSocketId(): string | undefined {
    return this.socket?.id
  }
}

// Export singleton instance
export const socketService = new SocketService()